import {
  Heading,
  LinkBox,
  LinkOverlay,
  Stack,
  Text,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';
import NextLink from 'next/link';
import * as React from 'react';

import { sponsor_content_clicked } from '@/analytics';
import { GlobeIcon, MapPinIcon, PhoneIcon } from '@/imports/ui/chakra/feather';

import { ProfileImage } from '@/components/CompanyHit/components/ProfileImage';
import {
  SponsorDirectory_Search_sponsorDirectorySearch_results,
  SponsorDirectory_Search_sponsorDirectorySearch_results_cardAttributes,
} from '../../graphql-types/SponsorDirectory_Search';

type SponsorDirectoryAddress = NonNullable<
  SponsorDirectory_Search_sponsorDirectorySearch_results['company']
>['address'];

const formatAddress = (address: SponsorDirectoryAddress | null): string | null => {
  if (!address) {
    return null;
  }
  const parts = [
    address.city,
    address.state,
    address.country,
  ].filter(Boolean);
  if (parts.length === 0 && address.streetAddress) {
    return address.streetAddress;
  }
  if (parts.length === 0) {
    return null;
  }
  return parts.join(', ');
};

export const SponsorDirectoryCompanyHit: React.FC<{
  sponsor: SponsorDirectory_Search_sponsorDirectorySearch_results;
  isFollowing?: boolean;
}> = ({ sponsor, isFollowing }) => {
  const addressLine = formatAddress(sponsor.company?.address ?? null);
  const profileImageSrc = sponsor.profilePictureUrl || undefined;

  return (
    <LinkBox>
      <Stack direction="row" spacing="4">
        <ProfileImage
          src={profileImageSrc}
          alt={sponsor.name}
          preview
        />
        <Stack flex="1" spacing="2">
          <Stack direction="row" align="center" spacing="2">
            <LinkOverlay
              as={NextLink}
              href={`/companies/${sponsor._id}`}
              onClick={() => {
                sponsor_content_clicked({
                  sponsor_id: sponsor._id,
                  sponsor_name: sponsor.name,
                  sponsor_tier: sponsor.sponsorTier?.name,
                  sponsor_content_type: 'directory_card',
                  sponsor_content_id: sponsor._id,
                  sponsor_interaction_source: 'sponsors_directory',
                  location_organization_id: sponsor._id,
                });
              }}
            >
              <Heading size="sm">{sponsor.name}</Heading>
            </LinkOverlay>
            {isFollowing && (
              <Text fontSize="sm" color="blue.500" fontWeight="bold">
                Following
              </Text>
            )}
          </Stack>
          {sponsor.aboutUsPlainText && (
            <Text color="gray.600" mt="4" mb="2">
              {sponsor.aboutUsPlainText}
            </Text>
          )}
          <Wrap color="gray.600" mt="2">
            {sponsor.company?.website && (
              <WrapItem>
                <Text wordBreak="break-word">
                  <GlobeIcon mr="1" boxSize={3} color="primary.500" />
                  {sponsor.company.website}
                </Text>
              </WrapItem>
            )}
            {sponsor.company?.phoneNumber && (
              <WrapItem>
                <Text>
                  <PhoneIcon mr="1" boxSize={3} color="primary.500" />
                  {sponsor.company.phoneNumber}
                </Text>
              </WrapItem>
            )}
            {addressLine && (
              <WrapItem>
                <Text>
                  <MapPinIcon mr="1" boxSize={3} color="primary.500" />
                  {addressLine}
                </Text>
              </WrapItem>
            )}
            {sponsor.cardAttributes.map(
              (attribute: SponsorDirectory_Search_sponsorDirectorySearch_results_cardAttributes) => (
              <WrapItem key={attribute.attributeSchemaId}>
                <Text color="gray.600">
                  {attribute.name}: {attribute.displayValues.join(', ')}
                </Text>
              </WrapItem>
            ))}
          </Wrap>
        </Stack>
      </Stack>
    </LinkBox>
  );
};
